import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../../Components/Sidebar";
import { useNavigate } from "react-router-dom";
import { PiTextColumnsLight } from "react-icons/pi";
import { TbAward } from "react-icons/tb";
import { MdOutlineDeleteForever } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import DeletePopup from "../../Components/DeletePopup";
import EditScholarshipPopup from "./EditScholarshipPopup";
import { deleteScholarship, fetchScholarships } from "../../redux/actions/scholarshipActions";
import { useDispatch, useSelector } from "react-redux";
import PageComponent from "../../Components/PageComponent";
import { toast } from "react-hot-toast";

function AllScholarships() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [refreshData, setRefreshData] = useState(false);

  const { data: scholarshipList = [], loading, error } = useSelector(
    (state) => state.scholarship.Scholarship || {}
  );

  useEffect(() => {
    dispatch(fetchScholarships());
  }, [dispatch, refreshData]);

  const handleDelete = (scholarship) => {
    setSelectedScholarship(scholarship);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedScholarship) {
      dispatch(deleteScholarship(selectedScholarship.id))
        .then(() => {
          toast.success("Scholarship deleted successfully");
          setIsDeleteOpen(false);
          setRefreshData((prev) => !prev);
        })
        .catch(() => {
          toast.error("Error deleting the scholarship.");
        });
    }
  };

  const handleEdit = (scholarship) => {
    setSelectedScholarship(scholarship);
    setIsEditOpen(true);
  };

  const handleSaveEdit = useCallback(() => {
    setRefreshData((prev) => !prev);
  }, []);

  if (loading) return <div className="text-center text-gray-500">Loading scholarships...</div>;
  if (error) return <div className="text-center text-red-500">Error: {error}</div>;

  return (
    <>
      <PageComponent>
        <div className="flex w-screen h-screen bg-[#F8F7FA] overflow-hidden">
          <div className="fixed top-0 left-0 w-64 h-full bg-white shadow-md overflow-y-auto">
            <Sidebar />
          </div>
          <div className="flex-1 min-h-screen p-6 ml-64 overflow-auto">
            <div className="flex items-center space-x-2 text-3xl text-gray-500">
              <span>Academics & International</span>
              <span>/</span>
              <span className="text-black">Scholarships</span>
            </div>

            <div className="flex items-center mt-4 space-x-4">
              <button
                className="flex items-center px-4 py-2 text-[#A8AAAE] rounded-lg shadow hover:bg-gray-100"
                onClick={() => navigate("/academics&international/scholarships/addScholarship")}
              >
                <TbAward className="mr-2" />
                Add Scholarship
              </button>
              <button
                className="flex items-center px-4 py-2 text-white bg-[#92288C] rounded-lg shadow hover:bg-[#6C0B67]"
                onClick={() => navigate("/academics&international/scholarships/allScholarships")}
              >
                <PiTextColumnsLight className="mr-2" />
                All Scholarships
              </button>
            </div>

            <div className="w-full p-6 mt-6 bg-white rounded-lg shadow-md">
              <h2 className="mb-4 text-xl font-semibold text-gray-700">Scholarships List</h2>
              <div className="overflow-x-auto max-h-96 sidebar-container">
                <table className="min-w-full border-collapse table-auto">
                  <thead>
                    <tr className="text-left text-gray-600 bg-gray-100">
                      <th className="px-8 py-2 whitespace-nowrap">IMAGE</th>
                      <th className="px-8 py-2 whitespace-nowrap">SCHOLARSHIP TITLE</th>
                      <th className="px-8 py-2 whitespace-nowrap">FILES</th>
                      <th className="px-8 py-2 whitespace-nowrap">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scholarshipList.length > 0 ? (
                      scholarshipList.map((scholarship, index) => (
                        <tr key={scholarship.id || index} className="border-b border-gray-300">
                          <td className="px-8 py-2">
                            {scholarship.image && scholarship.image[0] ? (
                              <img
                                src={scholarship.image[0]}
                                alt={scholarship.title}
                                className="object-cover w-12 h-12 rounded"
                              />
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-8 py-2">{scholarship.title || "N/A"}</td>
                          <td className="px-8 py-2">
                            {scholarship.files?.length || 0} file(s)
                          </td>
                          <td className="flex items-center justify-start px-2 py-2 gap-x-2">
                            <button
                              type="button"
                              className="flex items-center px-5 py-1 text-gray-400 bg-gray-100 border border-gray-400 rounded"
                              onClick={() => handleDelete(scholarship)}
                            >
                              Delete
                              <MdOutlineDeleteForever className="w-5 h-5 ml-2" />
                            </button>
                            <button
                              type="button"
                              className="flex items-center px-5 py-1 border rounded text-primary-color border-primary-color bg-primary-color bg-opacity-10"
                              onClick={() => handleEdit(scholarship)}
                            >
                              Edit
                              <FaRegEdit className="w-4 h-4 ml-2" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-8 py-4 text-center text-gray-500">
                          No scholarships available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <DeletePopup
              title="scholarship"
              isOpen={isDeleteOpen}
              onClose={() => setIsDeleteOpen(false)}
              onConfirm={handleDeleteConfirm}
              data={selectedScholarship?.title || ""}
            />

            <EditScholarshipPopup
              isOpen={isEditOpen}
              onClose={() => setIsEditOpen(false)}
              onSave={handleSaveEdit}
              scholarship={selectedScholarship || {}}
            />
          </div>
        </div>
      </PageComponent>
    </>
  );
}

export default AllScholarships;
